import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useUbi from '@di-shared/features/ubi/useUbi';
import clsx from 'clsx';
import {Navigate} from 'react-router-dom';
import {ROUTE_PATHS} from '@di-shared/routes';
import {UbiDetailPageProps} from '@di-shared/pages/ubi/[serviceId]';
import {AgreeInfoType, DiscountType} from '@di-shared/api/di/type';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {type ApiResponseGetUbiThirdPartyTermsResponse, ApiResponseListUbiThirdPartyAgreementsResponse} from '@di-shared/api/di/__generated';
import {WebviewUtils} from '@shared/webviewUtils';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import {emitEventLog} from '@di-shared/features/analytics';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

function UbiDetailContents({serviceId, vin}: UbiDetailPageProps) {
  const {t} = useInferableTranslation();
  const {convertDistanceSymbol, convertDistanceValue} = useUnitConverter();

  const [{data: ubiAgreementData, refetch, isError}, {data: termsData}] = useQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.ubiThirdPartyAgreementsController.listCciAgreements), vin],
        queryFn: () => diApi.ubiThirdPartyAgreementsController.listCciAgreements({vin}),
        select: ({data}: ApiResponseListUbiThirdPartyAgreementsResponse) => {
          return data.resMsg.find((serviceItem) => serviceItem.serviceId === serviceId);
        },
      },
      {
        queryKey: [getQueryKey(diApi.ubiThirdPartyTermsController.getAgreements1), vin, serviceId],
        queryFn: () => diApi.ubiThirdPartyTermsController.getAgreements1({vin, serviceId}),
        select: ({data}: ApiResponseGetUbiThirdPartyTermsResponse) => data,
      },
    ],
  });

  const {ubiData, displayInsuranceDetailData} = useUbi({vin, serviceId});
  const isAgree = !!ubiAgreementData && !isError;

  if (!displayInsuranceDetailData) {
    // FIXME : 404 PAGE NOT FOUND
    return (
      <Navigate
        to={ROUTE_PATHS.ERROR.getFullPath()}
        replace
      />
    );
  }

  async function openUbiAgreement() {
    // 이미 동의가 되어 있어서 바로 보험사 링크 이동
    if (isAgree) {
      WebviewUtils.openWebBrowser(displayInsuranceDetailData!.website);
    } else {
      const result = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_AGREE.getFullPath(), {
        vin,
        serviceId,
        termsData,
        agreeInfo:
          displayInsuranceDetailData?.discounts
            .map((discountItem) => AgreeInfoType[discountItem.agreeType as keyof typeof AgreeInfoType])
            ?.filter((discountItem) => discountItem != null) || [],
        companyName: displayInsuranceDetailData?.insuranceCompany,
      });
      if (result) {
        WebviewUtils.openWebBrowser(displayInsuranceDetailData!.website);
        await refetch();
      }
    }
  }

  async function withdrawUbiAgreement() {
    const result = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_UBI_DETAIL_$SERVICE_ID_WITHDRAW.getFullPath(), {
      vin,
      serviceId,
      agreeInfo:
        displayInsuranceDetailData?.discounts
          .map((discountItem) => AgreeInfoType[discountItem.agreeType as keyof typeof AgreeInfoType])
          ?.filter((discountItem) => discountItem != null) || [],
    });
    if (result) {
      WebviewUtils.showToast(t('T20183_$company', {company: displayInsuranceDetailData!.insuranceCompany}));
      await refetch();
    }
  }

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{displayInsuranceDetailData.insuranceCompany}</h1>
      </Header>

      <main className="content page_ubi">
        <section className="section">
          <ul className="contract_list">
            {displayInsuranceDetailData.discounts.map((discountItem, index) => (
              <li
                className="contract_item"
                key={index}
              >
                <div className="contract_heading">
                  <div className="contract_title">
                    <h2 className="contract_name">{discountItem.productName}&nbsp;</h2>
                    {discountItem.maxDiscountPercentage > 0 && (
                      <span className="contract_rate">
                        {discountItem.additionalDiscount
                          ? t('T20349_$discount', {
                              discount: `${discountItem.displayDiscountPercentage}`,
                            })
                          : t('T20042_$discount', {
                              discount: `${discountItem.displayDiscountPercentage}`,
                            })}
                      </span>
                    )}
                  </div>
                  {discountItem.agreeType === DiscountType.SAFE_DRIVING_SCORE &&
                    (ubiData.rangeDrvDist >= displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingDistance &&
                    ubiData.safetyDrvScore >= displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingScore ? (
                      <div className="badge_box">
                        <span className="badge badge_basic_container badge_20px">{t('T20046')}</span>
                      </div>
                    ) : (
                      <div className="badge_box">
                        <span className="badge badge_container_red badge_20px">{t('T20047')}</span>
                      </div>
                    ))}
                </div>
                <p className="contract_desc">{discountItem.discountDescription}</p>
                {discountItem.agreeType === DiscountType.SAFE_DRIVING_SCORE && (
                  <div className="section_container">
                    <div className="ubi_my">
                      <ul className="ubi_my_list">
                        <li
                          className={clsx('ubi_my_item', {
                            type_fail:
                              ubiData.rangeDrvDist < displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingDistance,
                          })}
                        >
                          <span className="ubi_my_key">{t('T20022')}</span>
                          <span className="ubi_my_val">
                            {convertDistanceValue(ubiData.rangeDrvDist)}
                            {convertDistanceSymbol(ubiData.rangeDrvDist)}
                          </span>
                          {ubiData.rangeDrvDist >= displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingDistance ? (
                            <span className="ubi_my_detail">{t('T20043')}</span>
                          ) : (
                            <span className="ubi_my_detail">
                              {t('T20044_$n', {
                                n: `${convertDistanceValue(
                                  displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingDistance - ubiData.rangeDrvDist
                                )}${convertDistanceSymbol(
                                  displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingDistance - ubiData.rangeDrvDist
                                )}`,
                              })}
                            </span>
                          )}
                        </li>
                        <li
                          className={clsx('ubi_my_item', {
                            type_fail:
                              ubiData.safetyDrvScore < displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingScore,
                          })}
                        >
                          <span className="ubi_my_key">{t('T20045')}</span>
                          <span className="ubi_my_val">{t('T20006_$score', {score: ubiData.safetyDrvScore})}</span>
                          {ubiData.safetyDrvScore >= displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingScore ? (
                            <span className="ubi_my_detail">{t('T20043')}</span>
                          ) : (
                            <span className="ubi_my_detail">
                              {t('T20044_$n', {
                                n: t('T20006_$score', {
                                  score:
                                    displayInsuranceDetailData.achievementCriteria.SAFE_DRIVING_SCORE.drivingScore - ubiData.safetyDrvScore,
                                }),
                              })}
                            </span>
                          )}
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={() => {
              emitEventLog({
                event: 'di_benefits',
                data_action: 'sign up for insurance',
                data_name: displayInsuranceDetailData.insuranceCompany,
              });
              openUbiAgreement();
            }}
          >
            {t('T20031')}
          </button>
          {isAgree && (
            <button
              type="button"
              className="contract_withdraw_btn"
              onClick={() => {
                emitEventLog({
                  event: 'di_benefits',
                  data_action: 'withdraw personal info',
                });
                withdrawUbiAgreement();
              }}
            >
              {t('T20032')}
            </button>
          )}
        </section>

        <div className="divider divider_thick"></div>
        <section className="section section_notice">
          <ul className="notice_list">
            <li className="notice_item">{t('T20028')}</li>
            <li className="notice_item">{t('T20029')}</li>
            <li className="notice_item">{t('T20030')}</li>
          </ul>
          <p
            className="notice_refer"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {displayInsuranceDetailData.reviewInfo.reviewText} ({displayInsuranceDetailData.reviewInfo.reviewStartDate}~
            {displayInsuranceDetailData.reviewInfo.reviewEndDate})
          </p>
        </section>
      </main>
    </div>
  );
}

export default UbiDetailContents;
